import { useEffect, useState, type ReactElement } from 'react';
import { ausschnitt, gainAt, lauterUm, nachStilleSchnitt, normalPegel, ohnePausen, pausen, stilleRaender, type Op, type TonEffekt, type Wellenform } from '@schnitt/shared';
import { pausenRaus } from '../edits';
import { useEditor } from '../store';
import { Sheet, Slider } from './Sheet';

/**
 * The sound of one clip, from the toolbar: level, mute, fades. "Ton" used
 * to be a mute switch and nothing else; a level is what people reach for.
 */
export function AudioSheet({ onClose }: { onClose: () => void }): ReactElement | null {
  const mehrere = useEditor((s) => s.selection.length > 1);
  return mehrere ? <MehrereTon onClose={onClose} /> : <EinTon onClose={onClose} />;
}

/**
 * **Der Ton mehrerer Clips zugleich** (7.209; Sascha, 4. Oktober:
 * „Lautstärke settings zusammen für alle Clips am Desktop“). Ein Regler,
 * der alle um dieselben dB hebt oder senkt -- er steht nach jedem Zug wieder
 * auf 0, weil er ein „um so viel“ ist und kein Wert --, dazu „Alle
 * normalisieren“ und „Alle stumm“.
 */
function MehrereTon({ onClose }: { onClose: () => void }): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const selection = useEditor((s) => s.selection);
  const dispatch = useEditor((s) => s.dispatch);
  const toast = useEditor((s) => s.toast);
  const assets = useEditor((s) => s.assets);
  const clips = timeline.tracks.flatMap((t) => t.clips).filter((c) => selection.includes(c.id));
  // Wovon der laufende Zug ausgeht: die Pegel beim Aufsetzen. So wird aus
  // „+3 dB“ nicht „+1, +2, +3 dB aufeinander“.
  const [zug, setZug] = useState<{ basis: typeof clips; db: number } | null>(null);
  const anfangen = () => {
    if (!zug) setZug({ basis: clips, db: 0 });
  };
  const ziehen = (db: number) => {
    const basis = zug?.basis ?? clips;
    setZug({ basis, db });
    dispatch(basis.map((c) => ({ op: 'updateClip', clipId: c.id, patch: lauterUm(c, db) }) as Op));
  };
  const loslassen = () => setZug(null);
  const alleStumm = clips.length > 0 && clips.every((c) => c.muted);
  const [rechnet, setRechnet] = useState(false);
  const normalisieren = async () => {
    setRechnet(true);
    try {
      const ops: Op[] = [];
      let mitVerlauf = 0;
      let still = 0;
      for (const c of clips) {
        const quelle = c.source;
        const url = quelle.kind === 'asset' ? assets.find((a) => a.id === quelle.assetId)?.waveformUrl : null;
        if (!url) continue;
        // Wie beim einzelnen Clip: einen Pegelverlauf wirft kein Knopf weg.
        if ((c.gainKeyframes ?? []).length > 0) {
          mitVerlauf++;
          continue;
        }
        const welle = (await fetch(url).then((r) => r.json())) as Wellenform;
        const pegel = normalPegel(ausschnitt(welle, c.sourceIn, c.sourceIn + c.duration * c.speed));
        if (pegel === null) {
          still++;
          continue;
        }
        ops.push({ op: 'updateClip', clipId: c.id, patch: { gain: pegel, muted: false } } as Op);
      }
      if (ops.length) dispatch(ops);
      const rest = [mitVerlauf ? `${mitVerlauf} mit Pegelverlauf übersprungen` : '', still ? `${still} ohne Ton` : ''].filter(Boolean).join(', ');
      toast(ops.length ? 'success' : 'info', `${ops.length} von ${clips.length} Clips normalisiert${rest ? ` (${rest})` : ''}.`);
    } catch {
      toast('error', 'Die Wellenform liess sich nicht laden.');
    } finally {
      setRechnet(false);
    }
  };
  const db = zug?.db ?? 0;
  return (
    <Sheet title={`Ton (${clips.length} Clips)`} onClose={onClose}>
      <div className="field" data-alle-ton>
        <div className="spread">
          <span>Alle lauter oder leiser</span>
          <strong data-alle-db>{db > 0 ? `+${db}` : db} dB</strong>
        </div>
        <input
          type="range"
          min={-12}
          max={12}
          step={1}
          value={db}
          onPointerDown={anfangen}
          onKeyDown={anfangen}
          onChange={(e) => ziehen(Number(e.target.value))}
          onPointerUp={loslassen}
          onKeyUp={loslassen}
          onBlur={loslassen}
          aria-label="Alle lauter oder leiser"
          data-alle-pegel
        />
        <p className="hint" style={{ margin: '4px 0 0' }}>
          Um dieselben dB für jeden -- leise und laute Clips bleiben zueinander, wie sie waren.
        </p>
      </div>
      <div className="chips" style={{ marginTop: 12 }}>
        <button className="chip" data-alle-normalisieren disabled={rechnet} onClick={() => void normalisieren()}>
          {rechnet ? 'Rechnet…' : 'Alle normalisieren'}
        </button>
        <button
          className={`chip ${alleStumm ? 'active' : ''}`.trim()}
          data-alle-stumm
          onClick={() => dispatch(clips.map((c) => ({ op: 'updateClip', clipId: c.id, patch: { muted: !alleStumm } }) as Op))}
        >
          {alleStumm ? 'Alle wieder an' : 'Alle stumm'}
        </button>
      </div>
    </Sheet>
  );
}

function EinTon({ onClose }: { onClose: () => void }): ReactElement | null {
  const timeline = useEditor((s) => s.timeline);
  const selection = useEditor((s) => s.selection);
  const dispatch = useEditor((s) => s.dispatch);
  const playhead = useEditor((s) => s.playhead);
  const toast = useEditor((s) => s.toast);
  const assets = useEditor((s) => s.assets);
  const clip = timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === selection[0]);
  const quelle = clip?.source;
  const asset = quelle?.kind === 'asset' ? assets.find((a) => a.id === quelle.assetId) : undefined;

  // Die Wellenform, die beim Einlesen entstanden ist. Sie liegt als Datei
  // neben dem Medium; hier wird sie nur gelesen, nicht gerechnet -- alles
  // andere hiesse, die Datei ein zweites Mal durchzuhören (7.22).
  const [welle, setWelle] = useState<Wellenform | null>(null);
  const wellenUrl = asset?.waveformUrl ?? null;
  useEffect(() => {
    if (!wellenUrl) {
      setWelle(null);
      return;
    }
    let weg = false;
    void fetch(wellenUrl)
      .then((r) => r.json() as Promise<Wellenform>)
      .then((d) => {
        if (!weg) setWelle(d);
      })
      .catch(() => setWelle(null));
    return () => {
      weg = true;
    };
  }, [wellenUrl]);

  if (!clip) return null;
  const patch = (p: Record<string, unknown>) => dispatch({ op: 'updateClip', clipId: clip.id, patch: p } as Op);

  // Werkzeuge für den Ton (S19): höchstens eines je Art, und was auf null
  // steht, kommt aus der Liste heraus statt als Nullwert darin zu bleiben.
  const tonEffekte: TonEffekt[] = clip.tonEffekte ?? [];
  const entrauschen = tonEffekte.find((e) => e.type === 'entrauschen');
  const kompressor = tonEffekte.find((e) => e.type === 'kompressor');
  const eq = tonEffekte.find((e) => e.type === 'equalizer');
  const tonSetzen = (effekt: TonEffekt) =>
    patch({ tonEffekte: [...tonEffekte.filter((e) => e.type !== effekt.type), effekt] });
  const tonWeg = (art: TonEffekt['type']) => patch({ tonEffekte: tonEffekte.filter((e) => e.type !== art) });

  // Der Pegelverlauf: Punkte über die Zeit statt eines Werts für den ganzen
  // Clip. Solange keiner gesetzt ist, gilt `gain` und der Regler ist der
  // Regler. Sobald einer da ist, verstellt der Regler den Punkt unter dem
  // Abspielkopf -- sonst hätte man zwei Bedienungen für dieselbe Sache.
  const punkte = [...(clip.gainKeyframes ?? [])].sort((a, b) => a.at - b.at);
  const verlauf = punkte.length > 0;
  const local = Math.min(clip.duration, Math.max(0, playhead - clip.start));
  const aufClip = playhead >= clip.start && playhead <= clip.start + clip.duration;
  const hier = punkte.find((k) => Math.abs(k.at - local) < 0.05) ?? null;
  const wert = verlauf ? gainAt(clip, local) : clip.gain;
  const pct = Math.round(wert * 100);

  const setzeWert = (g: number) => {
    if (!verlauf) {
      patch({ gain: g });
      return;
    }
    const ohneHier = punkte.filter((k) => k !== hier);
    patch({ gainKeyframes: [...ohneHier, { at: local, gain: g }].sort((a, b) => a.at - b.at) });
  };

  const punktSetzen = () => {
    if (!aufClip) {
      toast('info', 'Erst den Abspielkopf auf den Clip stellen.');
      return;
    }
    if (hier) {
      const rest = punkte.filter((k) => k !== hier);
      patch({ gainKeyframes: rest });
      toast('success', rest.length ? 'Pegelpunkt entfernt.' : 'Letzter Punkt weg, wieder fester Pegel.');
      return;
    }
    patch({ gainKeyframes: [...punkte, { at: local, gain: wert }].sort((a, b) => a.at - b.at) });
    toast('success', punkte.length === 0 ? 'Erster Pegelpunkt gesetzt. Der Regler verstellt jetzt ihn.' : 'Pegelpunkt gesetzt.');
  };
  return (
    <Sheet title="Ton" onClose={onClose}>
      <div className="field">
        <div className="spread">
          <span>Lautstärke</span>
          <strong data-gain>{clip.muted ? 'stumm' : `${pct} %`}</strong>
        </div>
        <input
          type="range"
          min={0}
          max={200}
          step={1}
          value={pct}
          disabled={clip.muted}
          onChange={(e) => setzeWert(Number(e.target.value) / 100)}
          aria-label="Lautstärke"
        />
      </div>
      <div className="chips" style={{ marginTop: 10 }}>
        {[50, 100, 150].map((v) => (
          <button key={v} className={`chip ${pct === v && !clip.muted ? 'active' : ''}`} onClick={() => { setzeWert(v / 100); if (clip.muted) patch({ muted: false }); }}>
            {v} %
          </button>
        ))}
        <button className={`chip ${clip.muted ? 'active' : ''}`} onClick={() => patch({ muted: !clip.muted })}>
          {clip.muted ? 'Ton an' : 'Stumm'}
        </button>
      </div>
      <div className="field" style={{ marginTop: 14 }}>
        <div className="spread">
          <span>Einblenden</span>
          <strong>{clip.fadeIn.toFixed(1)} s</strong>
        </div>
        <input type="range" min={0} max={3} step={0.1} value={clip.fadeIn} onChange={(e) => patch({ fadeIn: Number(e.target.value) })} aria-label="Einblenden" />
      </div>
      <div className="field">
        <div className="spread">
          <span>Ausblenden</span>
          <strong>{clip.fadeOut.toFixed(1)} s</strong>
        </div>
        <input type="range" min={0} max={3} step={0.1} value={clip.fadeOut} onChange={(e) => patch({ fadeOut: Number(e.target.value) })} aria-label="Ausblenden" />
      </div>
      <div className="field" style={{ marginTop: 14 }}>
        <div className="spread">
          <span>Pegelverlauf</span>
          <strong data-punkte>{verlauf ? `${punkte.length} ${punkte.length === 1 ? 'Punkt' : 'Punkte'}` : 'keiner'}</strong>
        </div>
        <div className="chips">
          <button className={`chip ${hier ? 'active' : ''}`} onClick={punktSetzen} data-pegelpunkt>
            {hier ? 'Punkt hier weg' : 'Punkt hier setzen'}
          </button>
          {verlauf ? (
            <button
              className="chip"
              onClick={() => {
                patch({ gainKeyframes: [] });
                toast('success', 'Verlauf gelöscht, wieder fester Pegel.');
              }}
            >
              Verlauf löschen
            </button>
          ) : null}
        </div>
        {verlauf ? (
          <p className="hint" style={{ marginTop: 6 }}>
            {aufClip
              ? `Bei ${local.toFixed(1)} s sind es ${pct} %. Der Regler oben verstellt diese Stelle; zwischen zwei Punkten geht es gerade hinüber.`
              : 'Erst den Abspielkopf auf den Clip stellen.'}
          </p>
        ) : null}
      </div>
      {/* Zwei Handgriffe, die man sonst von Hand macht: lauter ziehen, bis es
          passt, und die Stille vorn und hinten wegschneiden. Beide rechnen aus
          der Wellenform, die beim Einlesen ohnehin entsteht (7.22). */}
      {welle ? (
        <div className="field" style={{ marginTop: 14 }}>
          <span>Handgriffe</span>
          <div className="chips">
            <button
              className="chip"
              data-normalisieren
              onClick={() => {
                const teil = ausschnitt(welle, clip.sourceIn, clip.sourceIn + clip.duration * clip.speed);
                const neuerPegel = normalPegel(teil);
                if (neuerPegel === null) {
                  toast('info', 'Hier ist nichts zu hören.');
                  return;
                }
                if (verlauf) {
                  // Ein Pegelverlauf ist eine Entscheidung, die jemand von
                  // Hand getroffen hat. Ein Knopf, der sie stillschweigend
                  // wegwirft, ist kein Handgriff, sondern ein Verlust.
                  toast('info', 'Erst den Pegelverlauf löschen.');
                  return;
                }
                patch({ gain: neuerPegel, muted: false });
                toast('success', `Pegel auf ${Math.round(neuerPegel * 100)} %.`);
              }}
            >
              Normalisieren
            </button>
            <button
              className="chip"
              data-stille-weg
              onClick={() => {
                const bis = clip.sourceIn + clip.duration * clip.speed;
                const raender = stilleRaender(welle, clip.sourceIn, bis);
                if (!raender) {
                  toast('info', 'Vorn und hinten ist schon nichts Stilles.');
                  return;
                }
                patch(nachStilleSchnitt(clip, raender));
                toast(
                  'success',
                  `Vorn ${raender.vorne.toFixed(2)} s weg, hinten ${raender.hinten.toFixed(2)} s.`,
                );
              }}
            >
              Stille wegschneiden
            </button>
            {/* 7.66a: der erste Schritt zum Rohschnitt. Die Pausen stehen in
                der Wellenform; dafür braucht es kein Modell. */}
            <button
              className="chip"
              data-pausen-raus
              onClick={() => {
                if (verlauf) {
                  toast('info', 'Erst den Pegelverlauf löschen.');
                  return;
                }
                const bis = clip.sourceIn + clip.duration * clip.speed;
                const liste = pausen(welle, clip.sourceIn, bis);
                if (liste.length === 0) {
                  toast('info', 'Keine Pause länger als 0,6 s.');
                  return;
                }
                const stuecke = ohnePausen(clip, liste);
                const weg = clip.duration - stuecke.reduce((s, st) => s + st.duration, 0);
                dispatch(pausenRaus(timeline, clip.id, stuecke));
                toast('success', `${liste.length} ${liste.length === 1 ? 'Pause' : 'Pausen'} raus, ${weg.toFixed(2)} s kürzer, ${stuecke.length} Stücke.`);
              }}
            >
              Pausen raus
            </button>
          </div>
          {/* Spitze, nicht Lautheit: das ist ein Unterschied, den man hört,
              und er gehört dorthin, wo der Knopf steht. */}
          <p className="hint" style={{ marginTop: 6 }}>
            Normalisieren hebt die lauteste Stelle knapp unter die Grenze.
          </p>
        </div>
      ) : null}

      {/* Werkzeuge für den Ton (S19). Sascha: „Rauschminderung, Kompressor,
          Equalizer, Hall -- das, was eine Stimme sendefähig macht.“ Drei
          davon; Hall fehlt, weil ffmpeg dafür eine Impulsantwort bräuchte und
          das, was es sonst anbietet, ein Echo ist.
          Je ein Regler und nicht ein Dutzend: hinter der Zahl steckt ein Satz
          Werte, wie bei der Bildstabilisierung. */}
      <div className="field" data-tonwerkzeuge>
        <span>Werkzeuge</span>
      </div>
      <Slider
        label="Rauschen mindern"
        value={entrauschen?.type === 'entrauschen' ? entrauschen.staerke : 0}
        min={0}
        max={1}
        step={0.1}
        format={(v) => (v === 0 ? 'aus' : `${Math.round(v * 100)}%`)}
        onChange={(v) => (v === 0 ? tonWeg('entrauschen') : tonSetzen({ type: 'entrauschen', staerke: v }))}
      />
      <Slider
        label="Gleichmässig machen"
        value={kompressor?.type === 'kompressor' ? kompressor.staerke : 0}
        min={0}
        max={1}
        step={0.1}
        format={(v) => (v === 0 ? 'aus' : `${Math.round(v * 100)}%`)}
        onChange={(v) => (v === 0 ? tonWeg('kompressor') : tonSetzen({ type: 'kompressor', staerke: v }))}
      />
      {([
        ['tiefen', 'Tiefen'],
        ['mitten', 'Mitten'],
        ['hoehen', 'Höhen'],
      ] as const).map(([feld, label]) => (
        <Slider
          key={feld}
          label={label}
          value={eq?.type === 'equalizer' ? eq[feld] : 0}
          min={-12}
          max={12}
          step={0.5}
          format={(v) => (v === 0 ? '±0 dB' : `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(1).replace('.', ',')} dB`)}
          onChange={(v) => {
            const jetzt =
              eq?.type === 'equalizer' ? eq : { type: 'equalizer' as const, tiefen: 0, mitten: 0, hoehen: 0 };
            const naechst = { ...jetzt, [feld]: v };
            if (naechst.tiefen === 0 && naechst.mitten === 0 && naechst.hoehen === 0) tonWeg('equalizer');
            else tonSetzen(naechst);
          }}
        />
      ))}
      {tonEffekte.length > 0 ? (
        <p className="hint" data-ton-hinweis style={{ marginTop: 0 }}>
          Wird beim Export gerechnet — die Vorschau spielt den Clip so, wie er aufgenommen wurde.
        </p>
      ) : null}

      <p className="hint">
        Der Pegel gilt für diesen Clip. Der Pegel der ganzen Spur steht unter „Bearbeiten“.
      </p>
    </Sheet>
  );
}
